import { useState, useEffect, useRef, useCallback } from 'react';
import { createKalshiClient } from '../../lib/kalshiApi';
import type { KalshiOrder } from '../../lib/kalshiApi';
import { initAuth } from '../../lib/kalshiApi/kalshiAuth';
import { OrderMonitor } from '../../lib/trading/orderMonitor';
import { createUserStream, type UserStream } from '../../lib/trading/userStream';
import { createMtUserStream, type MtUserStream } from '../../lib/trading/mtUserStream';
import { PositionTracker } from '../../lib/trading/positionTracker';
import { QueueVelocityTracker, type VelocityResult } from '../../lib/trading/queueVelocity';
import type { KalshiOrderbook } from '../../types';
import type { NBAMarketRow, FillNotification, KalshiCredentials } from '../../types';
import { perfTracker } from '../../lib/perf-tracker';
import { playFillTone, playWhaleTone, playKaChingTone } from '../../lib/audio/tradeAudio';
import { playQueueFrontTone, playBestPriceTone } from '../../lib/audio/defenseAudio';
import { readSoundSettings } from '../../lib/audio/soundSettings';
import { resolveDisplayTitle } from '../../lib/kalshiTicker/displayTitle';
import { computeExposure } from '../../lib/risk/exposureLimits';
import { useTradeGuard, type TradeGuardConfig, type TradeGuardReturn } from './useTradeGuard';
import type { ActivityEvent } from '@/contexts/ActivityContext';
import { boundaryError } from '@/lib/infra/boundaryLog';

export interface OrderManagementParams {
  isConnected: boolean;
  apiClientRef: React.MutableRefObject<Awaited<ReturnType<typeof createKalshiClient>> | null>;
  credentials: KalshiCredentials | null;
  positionTrackerRef: React.MutableRefObject<PositionTracker>;
  marketsDataRef: React.MutableRefObject<Map<string, NBAMarketRow>>;
  refreshPositions: () => void;
  loadPositions: () => Promise<void>;
  setErrorMessage: React.Dispatch<React.SetStateAction<string | null>>;
  portfolioPollingEnabled: boolean;
  setPortfolioPollingEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  /** Optional callback to push events to ActivityContext */
  onActivity?: (event: Omit<ActivityEvent, 'id' | 'timestamp'>) => void;
  /** Callback to hydrate activity rail with historical events (deduplicates by orderId) */
  onHydrateActivity?: (events: Array<Omit<ActivityEvent, 'id'>>) => void;
  /** Trade guard configuration (order guards + exposure limits + rate monitor) */
  tradeGuardConfig?: TradeGuardConfig;
}

/**
 * Compute absolute queue depth for an order.
 * Sums all contracts at better prices + queue position at own price level.
 *
 * For BUY orders on a side, "better price" means higher price bids ahead in queue.
 * The orderbook levels for that side are sorted descending by price.
 */
export function computeAbsoluteQueueDepth(
  order: KalshiOrder,
  orderbook: KalshiOrderbook | null
): number | null {
  if (!orderbook || order.queue_position == null) return null;

  const px = order.side === 'yes' ? order.yes_price : order.no_price;
  if (px == null) return null;

  // Get the orderbook levels for this order's side
  const levels = order.side === 'yes' ? orderbook.yes : orderbook.no;
  if (!levels) return null;

  // Sum all contracts at prices strictly better (higher) than ours
  let depth = 0;
  for (const [levelPrice, levelQty] of levels) {
    if (levelPrice > px) {
      depth += levelQty;
    }
  }

  // Add our queue position at own price level
  depth += order.queue_position;

  return depth;
}

export interface PendingOrder {
  id: string;
  ticker: string;
  side: 'yes' | 'no';
  priceCents: number;
  contracts: number;
  status: 'pending';
  submittedAt: number;
  venue: 'kalshi' | 'polymarket';
}

export interface OrderManagementReturn {
  orders: KalshiOrder[];
  pendingOrders: PendingOrder[];
  /** Velocity data per order ID (contracts/sec toward front of queue) */
  velocityByOrderId: Record<string, VelocityResult>;
  /** Absolute queue depth per order ID */
  absoluteQueueByOrderId: Record<string, number>;
  fillNotifications: FillNotification[];
  showOrderTicket: boolean;
  orderTicketMarket: string | undefined;
  orderTicketPrice: number | undefined;
  showKillAllConfirm: boolean;
  setShowKillAllConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  portfolioRealtimeEnabled: boolean;
  /** Orderbook cache for external consumers (read-only) */
  obCache: React.MutableRefObject<Map<string, KalshiOrderbook>>;
  /** Trade guard state (rate status, today's stats, enabled) */
  tradeGuard: TradeGuardReturn;
  refreshOrders: () => Promise<void>;
  handleFill: (fill: FillNotification) => void;
  handleOrderPlaced: () => Promise<void>;
  handlePlaceKalshiDashboardOrder: (args: {
    marketTicker: string;
    side: 'yes' | 'no';
    priceCents: number;
    contracts: number;
    isTakerMode: boolean;
    expirationTs?: number;
  }) => Promise<void>;
  handleCancelOrder: (orderId: string) => Promise<void>;
  handleDecreaseOrder: (orderId: string, nextQuantity: number) => Promise<void>;
  handleKillAll: () => Promise<void>;
  openOrderTicket: (marketTicker?: string, price?: number) => void;
  closeOrderTicket: () => void;
  cleanupOrders: () => void;
}

export function useOrderManagement({
  isConnected,
  apiClientRef,
  credentials,
  positionTrackerRef,
  marketsDataRef,
  refreshPositions,
  loadPositions,
  setErrorMessage,
  portfolioPollingEnabled,
  setPortfolioPollingEnabled,
  onActivity,
  onHydrateActivity,
  tradeGuardConfig,
}: OrderManagementParams): OrderManagementReturn {
  const [orders, setOrders] = useState<KalshiOrder[]>([]);
  const [pendingOrders, setPendingOrders] = useState<PendingOrder[]>([]);

  // Refs for trade guard (updated by state, read by guard)
  const positionsForGuardRef = useRef(positionTrackerRef.current.getPositions());
  const ordersForGuardRef = useRef(orders);
  // Keep refs in sync
  ordersForGuardRef.current = orders;

  const tradeGuard = useTradeGuard(tradeGuardConfig, positionsForGuardRef, ordersForGuardRef);
  const [velocityByOrderId, setVelocityByOrderId] = useState<Record<string, VelocityResult>>({});
  const [absoluteQueueByOrderId, setAbsoluteQueueByOrderId] = useState<Record<string, number>>({});
  const [fillNotifications, setFillNotifications] = useState<FillNotification[]>([]);
  const [showOrderTicket, setShowOrderTicket] = useState(false);
  const [orderTicketMarket, setOrderTicketMarket] = useState<string | undefined>();
  const [orderTicketPrice, setOrderTicketPrice] = useState<number | undefined>();
  const [showKillAllConfirm, setShowKillAllConfirm] = useState(false);
  const [portfolioRealtimeEnabled, setPortfolioRealtimeEnabled] = useState(false);

  const orderMonitorRef = useRef<OrderMonitor | null>(null);
  const userStreamRef = useRef<UserStream | null>(null);
  const velocityTrackerRef = useRef(new QueueVelocityTracker({ ringSize: 120 }));
  /** Track order IDs that have received at least one fill (for first-sale ka-ching) */
  const filledOrderIdsRef = useRef<Set<string>>(new Set());
  /** Ensure historical fills are hydrated only once */
  const hydratedRef = useRef(false);
  /** Whether we've already seeded the activity feed with historical fills */
  const seededRef = useRef(false);
  /** Last orderbook fetch time per ticker (throttle to 15s) */
  const obFetchTimesRef = useRef(new Map<string, number>());
  /** Cached orderbooks per ticker */
  const obCacheRef = useRef(new Map<string, KalshiOrderbook>());
  /** Previous absolute queue depths per order ID (for queue-front transition detection) */
  const prevDepthsRef = useRef<Record<string, number>>({});
  /** Previous best-price boolean per order ID (for best-price transition detection) */
  const prevBestPriceRef = useRef<Record<string, boolean>>({});
  /** Cooldown timestamps per order ID for queue/best-price alerts (30s window) */
  const queueAlertCooldownsRef = useRef<Map<string, number>>(new Map());

  const refreshOrders = useCallback(async () => {
    if (!apiClientRef.current) return;
    if (!portfolioPollingEnabled) return;
    try {
      const ordersData = await apiClientRef.current.getOpenOrders();
      setOrders(ordersData);

      // Feed queue positions to velocity tracker
      const tracker = velocityTrackerRef.current;
      const now = Date.now();
      for (const order of ordersData) {
        if (order.queue_position != null) {
          tracker.record(order.order_id, order.queue_position, now);
        }
      }

      // Check for velocity spikes and emit activity events
      const spikes = tracker.checkAllSpikes();
      for (const spike of spikes) {
        const order = ordersData.find((o) => o.order_id === spike.orderId);
        const dirLabel = spike.velocity.direction === 'advancing' ? 'advancing' : 'retreating';
        const msg = `Queue ${dirLabel} at ${Math.abs(spike.velocity.velocity).toFixed(1)} contracts/sec`;
        onActivity?.({
          type: 'queue_velocity',
          message: order ? `${resolveDisplayTitle(order.ticker)} — ${msg}` : msg,
          orderId: spike.orderId,
          ticker: order?.ticker,
        });
      }

      // Compute velocity for all tracked orders and expose via state
      const velocities: Record<string, VelocityResult> = {};
      for (const order of ordersData) {
        const v = tracker.getVelocity(order.order_id);
        if (v) velocities[order.order_id] = v;
      }
      setVelocityByOrderId(velocities);

      // Clean up tracker for filled/cancelled orders
      const activeIds = new Set(ordersData.map((o) => o.order_id));
      for (const orderId of [...tracker['rings'].keys()]) {
        if (!activeIds.has(orderId)) tracker.remove(orderId);
      }

      // Fetch orderbooks for absolute queue depth (throttled to 15s per ticker)
      const api = apiClientRef.current;
      if (api && ordersData.length > 0) {
        const uniqueTickers = [...new Set(ordersData.map((o) => o.ticker))];
        const fetchTimes = obFetchTimesRef.current;
        const cache = obCacheRef.current;
        const OB_THROTTLE_MS = 15_000;

        // Fetch stale tickers (fire-and-forget, don't block refreshOrders)
        const staleTickers = uniqueTickers.filter(
          (t) => !fetchTimes.has(t) || now - fetchTimes.get(t)! >= OB_THROTTLE_MS
        );

        if (staleTickers.length > 0) {
          // Don't await — let it run in background
          Promise.allSettled(
            staleTickers.map(async (ticker) => {
              fetchTimes.set(ticker, now);
              const ob = await api.getOrderbook(ticker);
              if (ob) cache.set(ticker, ob);
            })
          ).then((results) => {
            // Report any orderbook fetch failures
            const failures = results.filter((r) => r.status === 'rejected');
            if (failures.length > 0) {
              onActivity?.({
                type: 'error',
                message: `Orderbook fetch failed for ${failures.length}/${staleTickers.length} tickers`,
              });
            }
            // Recompute absolute queue depths with updated orderbooks
            const depths: Record<string, number> = {};
            for (const order of ordersData) {
              const ob = cache.get(order.ticker) ?? null;
              const depth = computeAbsoluteQueueDepth(order, ob);
              if (depth != null) depths[order.order_id] = depth;
            }
            setAbsoluteQueueByOrderId(depths);
          });
        } else {
          // Use cached orderbooks
          const depths: Record<string, number> = {};
          for (const order of ordersData) {
            const ob = cache.get(order.ticker) ?? null;
            const depth = computeAbsoluteQueueDepth(order, ob);
            if (depth != null) depths[order.order_id] = depth;
          }
          setAbsoluteQueueByOrderId(depths);
        }

        // Clean up cache for tickers no longer in orders
        const activeTickers = new Set(uniqueTickers);
        for (const ticker of [...cache.keys()]) {
          if (!activeTickers.has(ticker)) {
            cache.delete(ticker);
            fetchTimes.delete(ticker);
          }
        }
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      if (
        msg.includes('API error: 401') ||
        msg.includes('"code":"authentication_error"') ||
        msg.includes('"code": "authentication_error"')
      ) {
        setPortfolioPollingEnabled(false);
        setErrorMessage(
          'Connected to market feed, but portfolio endpoints are unauthorized (401 authentication_error). ' +
            'This usually means you are using the wrong environment (prod vs demo) or your API key is not provisioned for trading/portfolio.'
        );
        onActivity?.({
          type: 'error',
          message: 'Portfolio polling stopped — 401 auth error. Orders and positions unavailable.',
        });
        return;
      }
      console.error('Failed to refresh orders:', error);
    }
  }, [
    apiClientRef,
    portfolioPollingEnabled,
    setErrorMessage,
    setPortfolioPollingEnabled,
    onActivity,
  ]);

  const handleFill = useCallback(
    (fill: FillNotification) => {
      setFillNotifications((prev) => [...prev, fill]);
      setTimeout(() => {
        setFillNotifications((prev) => prev.filter((f) => f.orderId !== fill.orderId));
      }, 5000);

      const market = marketsDataRef.current.get(fill.ticker);
      const currentPrice = market?.awayProb || market?.homeProb || null;
      positionTrackerRef.current.addFill(fill, currentPrice);

      const displayName = resolveDisplayTitle(fill.ticker);
      onActivity?.({
        type: 'order_filled',
        message: `${fill.quantity} contracts ${displayName} ${fill.side.toUpperCase()} @ ${fill.price}¢`,
        amount: (fill.quantity * fill.price) / 100,
        orderId: fill.orderId,
        ticker: fill.ticker,
        ...(fill.source ? { source: fill.source } : {}),
      });

      // Imperative sound — priority: whale > ka-ching (first fill) > standard fill
      const snd = readSoundSettings();
      if (snd.tradeSoundEnabled && fill.quantity >= (snd.minFillContracts || 0)) {
        const isFirstFill = !filledOrderIdsRef.current.has(fill.orderId);
        if (isFirstFill) filledOrderIdsRef.current.add(fill.orderId);

        if (fill.quantity >= 1000 && snd.whaleSoundEnabled) {
          playWhaleTone();
        } else if (isFirstFill && snd.firstSaleSoundEnabled) {
          playKaChingTone();
        } else {
          playFillTone();
        }
      }

      refreshPositions();
      refreshOrders();
    },
    [marketsDataRef, positionTrackerRef, refreshPositions, refreshOrders, onActivity]
  );

  // Init order monitor + user stream when connected.
  // OrderMonitor starts IMMEDIATELY (5s poll) so fills are never missed.
  // User stream is attempted in parallel for faster WS-based detection.
  useEffect(() => {
    if (!isConnected || !credentials || !apiClientRef.current) return;

    // Already have user stream
    if (userStreamRef.current) return;

    let cancelled = false;
    const apiClient = apiClientRef.current;

    // Load initial data
    refreshOrders();
    loadPositions();

    // Start OrderMonitor IMMEDIATELY — this is the reliable fill detector.
    // Polls getOpenOrders() + getFilledOrders() every 5s. Dedup in handleFill
    // prevents double-firing if WS also delivers the fill.
    if (!orderMonitorRef.current) {
      const monitor = new OrderMonitor();
      monitor.start(
        apiClient,
        {
          onFill: (fill) => handleFill(fill),
          onStatusChange: (orderId, oldStatus, newStatus) => {
            if (import.meta.env.DEV)
              console.log(`[monitor] Order ${orderId}: ${oldStatus} → ${newStatus}`);
            refreshOrders();
          },
          onAuthDegraded: () => {
            onActivity?.({
              type: 'error',
              message: 'Fill monitoring stopped — 401 auth error. Reconnect required.',
            });
          },
        },
        { pollIntervalMs: 2000 }
      );
      orderMonitorRef.current = monitor;
    }

    // Hydrate activity rail with recent fills (one-time on connection)
    if (onHydrateActivity && !hydratedRef.current) {
      hydratedRef.current = true;
      apiClient
        .getFilledOrders({ limit: 20 })
        .then((fills) => {
          if (cancelled || fills.length === 0) return;
          const events: Array<Omit<ActivityEvent, 'id'>> = fills.map((f) => {
            const label = resolveDisplayTitle(f.ticker);
            const priceCents = Math.round(f.price); // already cents from Kalshi API
            return {
              type: 'order_filled' as const,
              message: `${label} ${f.side.toUpperCase()} ${f.count} @ ${priceCents}¢`,
              ticker: f.ticker,
              orderId: f.order_id,
              amount: (priceCents * f.count) / 100,
              timestamp: new Date(f.created_at).getTime(),
            };
          });
          onHydrateActivity(events);
        })
        .catch(() => {
          /* non-critical */
        });
    }

    // Try to connect user stream (WS) in parallel for faster fill detection.
    // If it fails, OrderMonitor is already running as the primary detector.
    // @ai-note MT mode uses mtUserStream (server-routed), ST uses direct Kalshi WS.
    const isMt = import.meta.env.VITE_MULTI_TENANT === 'true';

    if (isMt) {
      // MT path: connect to relay's UserStreamRouter via /relay/mt-ws
      const mtJwt = localStorage.getItem('g1xl_mt_access_token');
      if (mtJwt) {
        const mtStream = createMtUserStream();
        // Store as UserStream-compatible ref (duck-typed: onFill, onOrderEvent, disconnect)
        (userStreamRef as React.MutableRefObject<MtUserStream | UserStream | null>).current =
          mtStream;
        mtStream.onFill((fill) => handleFill(fill));
        mtStream.onOrderEvent(() => {
          refreshOrders();
        });
        mtStream.onError((err) => {
          console.warn('MT user stream error:', err);
          setPortfolioRealtimeEnabled(false);
        });
        mtStream.connect(mtJwt);
        if (!cancelled) {
          setPortfolioRealtimeEnabled(true);
        }
      }
    } else {
      // ST path: direct Kalshi WS via relay proxy
      (async () => {
        try {
          const { privateKey: cryptoKey } = await initAuth(
            credentials.accessKeyId,
            credentials.privateKeyPem
          );

          if (cancelled) return;

          try {
            const userStream = createUserStream(true);
            userStreamRef.current = userStream;
            userStream.onFill((fill) => handleFill(fill));
            userStream.onOrderEvent(() => {
              refreshOrders();
            });

            userStream.onError((err) => {
              console.warn('User stream error:', err);
              setPortfolioRealtimeEnabled(false);
            });

            await userStream.connect(credentials.accessKeyId, cryptoKey, credentials.environment);
            userStream.subscribe();
            if (!cancelled) {
              setPortfolioRealtimeEnabled(true);
            }
          } catch (err) {
            console.warn('User stream unavailable, OrderMonitor is primary:', err);
            if (!cancelled) {
              setPortfolioRealtimeEnabled(false);
            }
          }
        } catch (err) {
          console.error('Order management auth init failed:', err);
        }
      })();
    }

    return () => {
      cancelled = true;
    };
  }, [
    isConnected,
    credentials,
    apiClientRef,
    handleFill,
    refreshOrders,
    loadPositions,
    setPortfolioPollingEnabled,
  ]);

  // Polling intervals
  useEffect(() => {
    if (!isConnected) return;
    if (!portfolioPollingEnabled) return;

    const ordersInterval = setInterval(() => {
      refreshOrders();
    }, 5000);

    const positionsIntervalMs = portfolioRealtimeEnabled ? 30000 : 5000;
    const positionsInterval = setInterval(() => {
      refreshPositions();
    }, positionsIntervalMs);

    return () => {
      clearInterval(ordersInterval);
      clearInterval(positionsInterval);
    };
  }, [
    isConnected,
    portfolioPollingEnabled,
    portfolioRealtimeEnabled,
    refreshOrders,
    refreshPositions,
  ]);

  // ── Top-of-book alerts: queue front + best price ─────────────────────
  useEffect(() => {
    if (orders.length === 0) {
      prevDepthsRef.current = {};
      prevBestPriceRef.current = {};
      return;
    }

    const snd = readSoundSettings();
    const now = Date.now();
    const COOLDOWN_MS = 30_000;
    const cooldowns = queueAlertCooldownsRef.current;
    const cache = obCacheRef.current;

    for (const order of orders) {
      const id = order.order_id;
      const depth = absoluteQueueByOrderId[id];

      // ── Queue Front: depth transitions from >0 to 0 ──
      if (depth != null) {
        const prevDepth = prevDepthsRef.current[id];
        if (prevDepth != null && prevDepth > 0 && depth === 0) {
          const lastAlert = cooldowns.get(`qf:${id}`) ?? 0;
          if (now - lastAlert >= COOLDOWN_MS) {
            cooldowns.set(`qf:${id}`, now);
            if (snd.queueFrontSoundEnabled) playQueueFrontTone();
            const px = order.side === 'yes' ? order.yes_price : order.no_price;
            onActivity?.({
              type: 'queue_front',
              message: `${resolveDisplayTitle(order.ticker)} — front of queue @ ${px}¢`,
              orderId: id,
              ticker: order.ticker,
            });
          }
        }
      }

      // ── Best Price: order's price becomes best bid on its side ──
      const ob = cache.get(order.ticker);
      if (ob) {
        const px = order.side === 'yes' ? order.yes_price : order.no_price;
        const levels = order.side === 'yes' ? ob.yes : ob.no;
        let bestBid = 0;
        if (levels) {
          for (const [levelPrice] of levels) {
            if (levelPrice > bestBid) bestBid = levelPrice;
          }
        }
        const isAtBest = px != null && bestBid > 0 && px === bestBid;
        const wasAtBest = prevBestPriceRef.current[id] ?? false;

        if (!wasAtBest && isAtBest) {
          const lastAlert = cooldowns.get(`bp:${id}`) ?? 0;
          if (now - lastAlert >= COOLDOWN_MS) {
            cooldowns.set(`bp:${id}`, now);
            if (snd.bestPriceSoundEnabled) playBestPriceTone();
            onActivity?.({
              type: 'best_price',
              message: `${resolveDisplayTitle(order.ticker)} — now best bid @ ${px}¢`,
              orderId: id,
              ticker: order.ticker,
            });
          }
        }

        prevBestPriceRef.current[id] = isAtBest;
      }
    }

    // Update depth refs for next cycle
    prevDepthsRef.current = { ...absoluteQueueByOrderId };

    // Clean up stale cooldown entries
    const activeIds = new Set(orders.map((o) => o.order_id));
    for (const key of [...cooldowns.keys()]) {
      const orderId = key.slice(3); // strip "qf:" or "bp:" prefix
      if (!activeIds.has(orderId)) cooldowns.delete(key);
    }
  }, [orders, absoluteQueueByOrderId, onActivity]);

  // Seed activity feed with recent filled orders on connect.
  // Runs once per session so the activity rail isn't empty on page load.
  useEffect(() => {
    if (!isConnected || !apiClientRef.current || seededRef.current) return;
    seededRef.current = true;

    const apiClient = apiClientRef.current;
    apiClient
      .getFilledOrders({ limit: 20 })
      .then((fills) => {
        if (!onActivity || fills.length === 0) return;

        // Get existing orderIds from persisted activity log to skip duplicates
        const existingOrderIds = new Set<string>();
        try {
          const raw = localStorage.getItem('g1xl-activity-log-v1');
          if (raw) {
            for (const e of JSON.parse(raw) as Array<{ orderId?: string }>) {
              if (e.orderId) existingOrderIds.add(e.orderId);
            }
          }
        } catch {
          // noop
        }

        // Push oldest first so newest ends up at top of the rail
        const newFills = fills.filter((f) => !existingOrderIds.has(f.order_id));
        for (const fill of [...newFills].reverse()) {
          const displayName = resolveDisplayTitle(fill.ticker);
          onActivity({
            type: 'order_filled',
            message: `${fill.count} contracts ${displayName} ${fill.side.toUpperCase()} @ ${fill.price}¢`,
            amount: (fill.count * fill.price) / 100,
            orderId: fill.order_id,
            ticker: fill.ticker,
          });
        }
      })
      .catch((err) => {
        console.warn('Failed to seed historical fills:', err);
      });
  }, [isConnected, apiClientRef, onActivity]);

  const handleOrderPlaced = useCallback(async () => {
    await refreshOrders();
    if (orderMonitorRef.current) {
      await orderMonitorRef.current.checkImmediate();
    }
  }, [refreshOrders]);

  const handlePlaceKalshiDashboardOrder = useCallback(
    async (args: {
      marketTicker: string;
      side: 'yes' | 'no';
      priceCents: number;
      contracts: number;
      isTakerMode: boolean;
      expirationTs?: number;
    }) => {
      const api = apiClientRef.current;
      if (!api) throw new Error('Not connected');

      // Add optimistic pending order immediately
      const pendingId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const pending: PendingOrder = {
        id: pendingId,
        ticker: args.marketTicker,
        side: args.side,
        priceCents: args.priceCents,
        contracts: args.contracts,
        status: 'pending',
        submittedAt: Date.now(),
        venue: 'kalshi',
      };
      setPendingOrders((prev) => [...prev, pending]);

      // ── Trade Guard Gate ──────────────────────────────────────────
      // Validate order before submission (duplicate, slippage, size, exposure, rate)
      positionsForGuardRef.current = positionTrackerRef.current.getPositions();
      const guardResult = tradeGuard.validateOrder({
        ticker: args.marketTicker,
        side: args.side,
        priceCents: args.priceCents,
        quantity: args.contracts,
      });

      if (!guardResult.ok) {
        const valueDollars = computeExposure(args.contracts, args.priceCents, args.side);
        tradeGuard.logTrade({
          ts: new Date().toISOString(),
          action: 'place',
          ticker: args.marketTicker,
          side: args.side,
          count: args.contracts,
          priceCents: args.priceCents,
          valueDollars,
          result: 'blocked',
          reason: guardResult.reason,
        });
        onActivity?.({
          type: 'error',
          message: `Order blocked: ${guardResult.reason}`,
          ticker: args.marketTicker,
        });
        // Remove the optimistic pending order
        setPendingOrders((prev) => prev.filter((p) => p.id !== pendingId));
        throw new Error(`Order blocked: ${guardResult.reason}`);
      }

      // ── Expiry Guard ─────────────────────────────────────────────
      if (args.expirationTs !== undefined) {
        const nowSec = Math.floor(Date.now() / 1000);
        if (args.expirationTs < nowSec + 30) {
          setPendingOrders((prev) => prev.filter((p) => p.id !== pendingId));
          throw new Error('Expiry time is in the past — change the expiry date or use GTC');
        }
      }

      const token = perfTracker.beginSpan('order', 'place-order', {
        ticker: args.marketTicker,
        side: args.side,
        contracts: args.contracts,
      });
      try {
        await api.placeOrder({
          ticker: args.marketTicker,
          side: args.side,
          action: 'buy',
          type: 'limit',
          count: args.contracts,
          price: args.priceCents,
          post_only: !args.isTakerMode,
          time_in_force: 'good_till_canceled',
          ...(args.expirationTs !== undefined ? { expiration_ts: args.expirationTs } : {}),
        });
        perfTracker.endSpan(token);

        const valueDollars = computeExposure(args.contracts, args.priceCents, args.side);
        tradeGuard.logTrade({
          ts: new Date().toISOString(),
          action: 'place',
          ticker: args.marketTicker,
          side: args.side,
          count: args.contracts,
          priceCents: args.priceCents,
          valueDollars,
          result: 'success',
        });

        onActivity?.({
          type: 'order_placed',
          message: `${args.contracts} contracts ${args.marketTicker} ${args.side.toUpperCase()} @ ${args.priceCents}¢`,
          amount:
            (args.contracts * (args.side === 'yes' ? args.priceCents : 100 - args.priceCents)) /
            100,
          ticker: args.marketTicker,
        });

        await refreshOrders();
        refreshPositions();
      } catch (err) {
        perfTracker.endSpan(token, { error: String(err) });
        const message = err instanceof Error ? err.message : String(err);

        // Log full request body so we can diagnose what was sent
        boundaryError('api-shape', `Order placement failed: ${message}`, {
          ticker: args.marketTicker,
          side: args.side,
          priceCents: args.priceCents,
          contracts: args.contracts,
          isTakerMode: args.isTakerMode,
          expirationTs: args.expirationTs,
        });

        // Only log as error if not already logged as blocked
        if (!message.startsWith('Order blocked:')) {
          const valueDollars = computeExposure(args.contracts, args.priceCents, args.side);
          tradeGuard.logTrade({
            ts: new Date().toISOString(),
            action: 'place',
            ticker: args.marketTicker,
            side: args.side,
            count: args.contracts,
            priceCents: args.priceCents,
            valueDollars,
            result: 'error',
            reason: message,
          });
        }

        onActivity?.({
          type: 'error',
          message: `Order failed: ${message}`,
          ticker: args.marketTicker,
        });
        throw err;
      } finally {
        // Remove pending order regardless of success/failure
        setPendingOrders((prev) => prev.filter((p) => p.id !== pendingId));
      }
    },
    [apiClientRef, refreshOrders, refreshPositions, onActivity, positionTrackerRef, tradeGuard]
  );

  const handleKillAll = useCallback(async () => {
    if (!apiClientRef.current) return;

    const orderCount = orders.length;
    try {
      await apiClientRef.current.cancelAllOrders();
      onActivity?.({
        type: 'order_cancelled',
        message: `Kill all — ${orderCount} order${orderCount !== 1 ? 's' : ''} cancelled`,
      });
      await refreshOrders();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to cancel orders';
      setErrorMessage(message);
    }
  }, [apiClientRef, orders.length, refreshOrders, setErrorMessage, onActivity]);

  const handleCancelOrder = useCallback(
    async (orderId: string) => {
      if (!apiClientRef.current) return;
      const token = perfTracker.beginSpan('order', 'cancel-order', { orderId });
      const cancelledOrder = orders.find((o) => o.order_id === orderId);
      try {
        await apiClientRef.current.cancelOrder(orderId);
        perfTracker.endSpan(token);
        const cancelLabel = cancelledOrder
          ? resolveDisplayTitle(cancelledOrder.ticker)
          : `Order ${orderId.slice(0, 8)}…`;
        onActivity?.({
          type: 'order_cancelled',
          message: `${cancelLabel} cancelled`,
          orderId,
        });
        velocityTrackerRef.current.remove(orderId);
        await refreshOrders();
      } catch (error) {
        perfTracker.endSpan(token, { error: String(error) });
        const message = error instanceof Error ? error.message : 'Failed to cancel order';
        setErrorMessage(message);
      }
    },
    [apiClientRef, orders, refreshOrders, setErrorMessage, onActivity]
  );

  const handleDecreaseOrder = useCallback(
    async (orderId: string, nextQuantity: number) => {
      if (!apiClientRef.current) return;
      try {
        await apiClientRef.current.decreaseOrder(orderId, { reduce_to: nextQuantity });
        await refreshOrders();
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to decrease order quantity';
        setErrorMessage(message);
        throw error;
      }
    },
    [apiClientRef, refreshOrders, setErrorMessage]
  );

  const openOrderTicket = useCallback((marketTicker?: string, price?: number) => {
    setOrderTicketMarket(marketTicker);
    setOrderTicketPrice(price);
    setShowOrderTicket(true);
  }, []);

  const closeOrderTicket = useCallback(() => {
    setShowOrderTicket(false);
    setOrderTicketMarket(undefined);
    setOrderTicketPrice(undefined);
  }, []);

  const cleanupOrders = useCallback(() => {
    if (userStreamRef.current) {
      userStreamRef.current.disconnect();
      userStreamRef.current = null;
    }
    if (orderMonitorRef.current) {
      orderMonitorRef.current.stop();
      orderMonitorRef.current = null;
    }
    setOrders([]);
    setFillNotifications([]);
    setPortfolioRealtimeEnabled(false);
  }, []);

  return {
    orders,
    pendingOrders,
    velocityByOrderId,
    absoluteQueueByOrderId,
    fillNotifications,
    showOrderTicket,
    orderTicketMarket,
    orderTicketPrice,
    showKillAllConfirm,
    setShowKillAllConfirm,
    portfolioRealtimeEnabled,
    obCache: obCacheRef,
    tradeGuard,
    refreshOrders,
    handleFill,
    handleOrderPlaced,
    handlePlaceKalshiDashboardOrder,
    handleCancelOrder,
    handleDecreaseOrder,
    handleKillAll,
    openOrderTicket,
    closeOrderTicket,
    cleanupOrders,
  };
}
